import { Box, Stack, Typography, useTheme } from "@mui/material";
import { languageLabel } from "@pages:app/audio/components/language";
import { RecordingCard } from "@pages:app/audio/components/RecordingCard";
import { RecordingPlayer } from "@pages:app/audio/components/RecordingPlayer";
import { ResultActions } from "@pages:app/audio/components/ResultActions";
import { SummaryPanel } from "@pages:app/audio/components/SummaryPanel";
import { Transcript } from "@pages:app/audio/components/Transcript";
import { speakerColor } from "@pages:app/audio/const";
import {
  AudioPlayer,
  TranscriptionJob,
  useTranscriptExport,
} from "@pages:app/audio/hooks";
import {
  baseName,
  detectedLanguage,
  formatDuration,
  groupIntoTurns,
  positionAt,
  renumberSpeakers,
  segmentsOf,
  speakerNumbersForBars,
  transcriptToText,
} from "@pages:app/audio/util";
import { formatFileSize } from "@utils:file";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface ResultViewProps {
  job: TranscriptionJob;
  player: AudioPlayer;
  botId: string;
  canSummarize: boolean;
}

/*
 * A completed transcription: the recording with its actions, then the conversation
 * and, when the bot can summarize, the summary beside it.
 */
export const ResultView = ({
  job,
  player,
  botId,
  canSummarize,
}: ResultViewProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { file, result, waveform } = job;

  const { turns, numbers, transcript, durationSeconds, language } =
    useMemo(() => {
      const segments = segmentsOf(result?.result);
      return {
        turns: groupIntoTurns(segments),
        numbers: renumberSpeakers(segments),
        transcript: transcriptToText(result?.result, (n) =>
          t("audio.speaker", { n }),
        ),
        durationSeconds: segments.length
          ? segments[segments.length - 1].end
          : 0,
        language: detectedLanguage(segments),
      };
    }, [result, t]);

  const title = file ? baseName(file.name) : t("audio.result.fallbackTitle");

  const length =
    player.duration || waveform?.durationSeconds || durationSeconds;

  // Playback: the word being spoken, and the speaker behind each bar of the strip
  const active = useMemo(
    () => (player.ready ? positionAt(turns, player.currentTime) : null),
    [turns, player.ready, player.currentTime],
  );
  const barSpeakers = useMemo(
    () =>
      speakerNumbersForBars(
        turns,
        numbers,
        length,
        waveform?.peaks.length ?? 0,
      ),
    [turns, numbers, length, waveform?.peaks.length],
  );
  const barColor = useCallback(
    (index: number) => {
      const speaker = barSpeakers[index];
      if (speaker === null || speaker === undefined) return null;
      return speaker === 0
        ? theme.palette.primary.main
        : speakerColor(speaker - 1);
    },
    [barSpeakers, theme.palette.primary.main],
  );
  const seekAndPlay = useCallback(
    (seconds: number) => {
      player.seek(seconds);
      player.play();
    },
    [player],
  );
  const meta = [
    file ? formatFileSize(file.size) : undefined,
    length > 0 ? formatDuration(length) : undefined,
    languageLabel(language, t),
    numbers.size > 0 ? t("audio.speakers", { count: numbers.size }) : undefined,
  ]
    .filter(Boolean)
    .join(", ");

  // The summary lives here so the document exports can include it
  const [summary, setSummary] = useState<string>("");
  const exporter = useTranscriptExport(botId, title, transcript, summary);

  return (
    <Stack spacing={4}>
      <RecordingCard
        file={file}
        meta={meta}
        top={
          <RecordingPlayer
            player={player}
            waveform={waveform}
            barColor={barColor}
            reveal
          />
        }
      >
        <ResultActions
          file={file}
          title={title}
          requestedLanguage={job.language}
          requestedSpeakers={job.numSpeakers}
          taskId={job.taskId}
          transcript={transcript}
          result={result}
          onExport={exporter.exportAs}
          isExporting={exporter.isExporting}
          onNew={job.reset}
        />
      </RecordingCard>
      {exporter.error && (
        <Typography
          role="alert"
          sx={{ color: "error.main", fontSize: 14, mt: -2 }}
        >
          {exporter.error}
        </Typography>
      )}

      <Box
        sx={{
          display: "grid",
          gap: { xs: 4, md: 5 },
          alignItems: "start",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            md: canSummarize
              ? "minmax(0, 1.6fr) minmax(320px, 1fr)"
              : "minmax(0, 1fr)",
          },
        }}
      >
        <Transcript
          turns={turns}
          numbers={numbers}
          active={active}
          isPlaying={player.isPlaying}
          onSeek={player.ready ? seekAndPlay : undefined}
        />
        {canSummarize && (
          <Box sx={{ position: { md: "sticky" }, top: { md: 88 } }}>
            <SummaryPanel
              transcript={transcript}
              botId={botId}
              summary={summary}
              onSummarized={setSummary}
            />
          </Box>
        )}
      </Box>
    </Stack>
  );
};
